import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getAllStudentCourses } from "../../../services/course.service";
import { Skeleton, DashboardSkeleton } from "./ui/Skeleton";

import DashboardSemesterContent from "./DashBoardSemContentV2";

export default function DashboardSemester({ setActiveSection, onCourseClick }) {
  const [semesterTabs, setSemesterTabs] = useState([]);
  const [activeSemester, setActiveSemester] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const data = await getAllStudentCourses();

        const semNumbersWithCourses = [...new Set(data.courses
          .map(course => course.semNumber || course.semester?.semNumber)
          .filter(semNum => semNum != null)
        )].sort((a, b) => a - b);

        const maxSemNumber = semNumbersWithCourses.length > 0
          ? Math.max(...semNumbersWithCourses, 8)
          : 8;

        const tabs = [];
        for (let i = 1; i <= maxSemNumber; i++) {
          const hasCourses = semNumbersWithCourses.includes(i);
          tabs.push({
            semNumber: i,
            name: `SEM ${i}`,
            accessible: hasCourses
          });
        }

        setSemesterTabs(tabs);

        if (semNumbersWithCourses.length > 0) {
          setActiveSemester(semNumbersWithCourses[0]);
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  if (loading) {
    return (
      <div className="mx-auto p-4 sm:p-6">
        {/* Header skeleton */}
        <div className="my-4 lg:my-4 mt-16 lg:mt-4">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-1 w-12 mt-2" />
        </div>

        {/* Tab strip skeleton */}
        <div className="mb-6">
          <div className="flex gap-2 pb-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-20 flex-shrink-0" />
            ))}
          </div>
        </div>

        {/* Content skeleton */}
        <DashboardSkeleton />
      </div>
    );
  }

  return (
    <div className="mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="my-4 lg:my-4 mt-16 lg:mt-4">
        <h1 className="text-headline font-black tracking-tight dark:text-white text-gray-800">
          Student Dashboard
        </h1>
        <div className="h-1 w-12 bg-accent1 rounded-sm mt-2" />
      </div>

      {/* Semester Tab Strip */}
      <div className="mb-6">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
          {semesterTabs.map((semester) => {
            const isActive = activeSemester === semester.semNumber;
            const isAccessible = semester.accessible;

            return (
              <button
                key={semester.semNumber}
                onClick={() => isAccessible && setActiveSemester(semester.semNumber)}
                disabled={!isAccessible}
                className={`
                  relative flex-shrink-0 px-4 py-2.5 text-sm font-bold uppercase tracking-wider
                  rounded-sm border transition-all duration-200
                  ${isActive
                    ? "bg-accent1 text-white border-accent1 shadow-sm"
                    : isAccessible
                      ? "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-none hover:border-accent1/50 hover:text-accent1 dark:hover:text-accent1 cursor-pointer"
                      : "bg-gray-50 dark:bg-gray-900 text-gray-300 dark:text-gray-600 border-gray-100 dark:border-gray-800 cursor-not-allowed opacity-50"
                  }
                `}
              >
                {semester.name}
                {isActive && (
                  <motion.div
                    layoutId="semester-indicator"
                    className="absolute -bottom-0.5 left-2 right-2 h-0.5 bg-white rounded-sm"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Semester Content */}
      <AnimatePresence mode="wait">
        {activeSemester && (
          <motion.div
            key={activeSemester}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            <DashboardSemesterContent
              setActiveSection={setActiveSection}
              semNumber={activeSemester}
              onCourseClick={onCourseClick}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty state when no semester is active */}
      {!activeSemester && (
        <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-sm p-12 text-center">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            Select a semester to view your dashboard
          </p>
        </div>
      )}
    </div>
  );
}
